import type { CabinetShape, CaseworkDescriptor } from "building-types/casework/caseworkDescriptor";
import { CabinetType, CaseworkSuperType, ShelvingType } from "building-types/casework/caseworkTypesEnum";
import type {
  WallCasework,
  WallCaseworkDynamicDeprecated,
  WallCaseworkFixed,
  WallCaseworkFixedDeprecated,
} from "building-types/casework/wallCaseworkTypes";

export function isCaseWorkEntity(entity: any): entity is WallCasework {
  return entity.entityType === "CASEWORK";
}

export function isDeprecatedCasework(
  casework: WallCasework,
): casework is WallCaseworkFixedDeprecated | WallCaseworkDynamicDeprecated {
  return casework.type === "FIXED" || casework.type === "DYNAMIC";
}

export function isFixedCasework(casework: WallCasework): casework is WallCaseworkFixed | WallCaseworkFixedDeprecated {
  return casework.type === "FIXED" || casework.type === "FIXED_V2";
}

/**
 * The box shape in effect: the instance's own `shape` wins over its catalog descriptor's, and
 * shelving has none. Shared by the inspector and generation so the two can never disagree.
 */
export function resolvedCabinetShape(
  casework: WallCasework,
  descriptor: CaseworkDescriptor | undefined,
): CabinetShape | undefined {
  if (casework.shape) {
    return casework.shape;
  }

  return descriptor?.superType === CaseworkSuperType.CABINET ? descriptor.shape : undefined;
}

/**
 * Whether a casework type rests on the floor and therefore covers the finish beneath it. Drives the
 * `removeFloorMaterialUnderBaseCabinets` setting — add new floor-standing casework types here and the
 * setting picks them up everywhere.
 *
 * Wall-hung types (upper cabinets, closet shelves) return false, as does untyped generic casework,
 * whose footprint is not necessarily a cabinet.
 */
export function caseworkCoversFloor(caseworkType: CabinetType | ShelvingType | undefined): boolean {
  switch (caseworkType) {
    case CabinetType.BASE_CABINET:
    case CabinetType.TALL_CABINET:
    case CabinetType.FILLER_PANEL:
      return true;
    case CabinetType.UPPER_CABINET:
    case ShelvingType.CLOSET_SHELF:
    case undefined:
      return false;
  }
}
